import { Directive, Input, TemplateRef, ViewContainerRef } from "@angular/core";
// eslint-disable-next-line boundaries/element-types
import { FeaturesService } from "@kno2/data-access/api-kno2fy-main";
import { AuthorizationToAccessItem, checkHasAuthorizationToAccess } from "@kno2/utils";
import { Subject } from "rxjs";
import { debounceTime, switchMap } from "rxjs/operators";

@Directive({
    selector: "[kno2FeatureToggle]",
    standalone: false
})
export class FeatureToggleDirective {
    private requiredFeatures: Array<string> | Array<AuthorizationToAccessItem>;
    private isStrict = true;
    private legacyMode = true;
    private streamChanges = false;
    private render$ = new Subject<void>();

    // example of implementation: *kno2FeatureToggle="['your-features'];strict:false"
    @Input() public set kno2FeatureToggleStrict(isStrict: boolean) {
        if (typeof isStrict !== "boolean") return;

        this.isStrict = isStrict;
        this.render$.next();
    }

    /**
     * Set the mode of operation for requesting the enabled features. If true, legacy mode
     * will request to the kno2 features API route. Otherwise, the directive calls to launch darkly
     */
    @Input() public set kno2FeatureToggleLegacyMode(legacyMode: boolean) {
        if (typeof legacyMode !== "boolean") return;

        this.legacyMode = legacyMode;
        this.render$.next();
    }

    @Input() public set kno2FeatureToggleStreamChanges(streamChanges: boolean) {
        if (typeof streamChanges !== "boolean") return;

        this.streamChanges = streamChanges;
        this.render$.next();
    }

    @Input() public set kno2FeatureToggle(requiredFeatures: Array<string> | Array<AuthorizationToAccessItem>) {
        this.requiredFeatures = requiredFeatures;
        this.render$.next();
    }

    constructor(
        private featuresService: FeaturesService,
        private templateRef: TemplateRef<any>,
        private viewContainer: ViewContainerRef
    ) {
        this.render$
            .pipe(
                debounceTime(20),
                switchMap(() => this.featuresService.getEnabledFeatures$())
            )
            .subscribe((enabledFeatures: Array<string>) => {
                const hasRequiredFeatures = checkHasAuthorizationToAccess(enabledFeatures, this.requiredFeatures, this.isStrict);

                this.render(hasRequiredFeatures);
            });
    }

    private render(hasFeature: boolean): void {
        this.viewContainer.clear();

        if (hasFeature) this.viewContainer.createEmbeddedView(this.templateRef);
    }
}
